<template>
  <div
    :class="['email-component', 'header-component', { selected }]"
    :style="computedStyles"
    @click="handleClick"
  >
    <div class="header-content">
      <div v-if="componentData.imageSrc || componentData.uploadedUrl" class="header-image-wrapper">
        <img
          :src="getImageSource()"
          :alt="componentData.imageAlt || 'Header Logo'"
          :style="imageStyles"
          class="header-image"
          @error="handleImageError"
        />
      </div>
      <div v-else class="header-placeholder" @click.stop="openImageUpload">
        <ImageEditor
          :edit="false"
          text="Add Logo"
          size="sm"
          :show_slider="false"
          :show-background-color="false"
          :show-description="false"
          :show-fit="false"
          @saveImage="handleImageUpload"
          @clickUpload="onImageUploadClick"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ImageEditor from '@/components/ImageEditor'

export default {
  name: 'HeaderComponent',
  components: {
    ImageEditor
  },
  props: ['componentData', 'selected'],
  computed: {
    computedStyles() {
      const styles = this.componentData.styles || {}
      return {
        backgroundColor: styles.backgroundColor || '#1e3a8a',
        width: '100%',
        margin: '0',
        padding: `${styles.paddingTop || '20'}px ${styles.paddingLeft || '20'}px ${styles.paddingBottom || '20'}px ${styles.paddingRight || '20'}px`,
        textAlign: styles.textAlign || 'center',
        minHeight: '60px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: styles.textAlign === 'left' ? 'flex-start' :
          styles.textAlign === 'right' ? 'flex-end' : 'center',
        boxSizing: 'border-box',
        position: 'relative'
      }
    },

    imageStyles() {
      const styles = this.componentData.styles || {}
      return {
        maxWidth: `${styles.imageWidth || '200'}px`,
        height: 'auto',
        display: 'block'
      }
    }
  },
  methods: {
    handleClick() {
      this.$emit('click')
    },

    getImageSource() {
      // Use uploaded S3 URL if available, fallback to imageSrc
      return this.componentData.uploadedUrl ||
        this.componentData.imageSrc ||
        ''
    },

    handleImageError(event) {
      // Fallback to placeholder if image fails to load
      console.error('Header image failed to load')
    },

    getCurrentImageData() {
      // Return stored ImageEditor data or create new one
      return this.componentData.imageEditorData || {
        url: this.componentData.uploadedUrl || this.componentData.imageSrc,
        description: this.componentData.imageAlt || '',
        scale: 100,
        background_color: '#e8e7ec',
        object_fit: 'cover',
        x: 0,
        y: 0,
        image_id: this.componentData.imageMetadata?.imageId || ''
      }
    },

    handleImageUpload(imageData) {
      // Store the uploaded URL
      this.$set(this.componentData, 'uploadedUrl', imageData.url)
      this.$set(this.componentData, 'sourceType', 'upload')

      // Store ImageEditor specific data for re-editing
      this.$set(this.componentData, 'imageEditorData', {
        url: imageData.url,
        description: imageData.description || '',
        scale: imageData.scale || 100,
        background_color: imageData.background_color || '#e8e7ec',
        object_fit: imageData.object_fit || 'cover',
        x: imageData.x || 0,
        y: imageData.y || 0,
        image_id: imageData.image_id || ''
      })

      // Use description as alt text if provided
      if (imageData.description) {
        this.$set(this.componentData, 'imageAlt', imageData.description)
      }

      // Store metadata
      this.$set(this.componentData, 'imageMetadata', {
        uploadedAt: new Date().toISOString(),
        originalFile: imageData.file || null,
        imageId: imageData.image_id || null
      })

      this.$emit('update')

      // Show success notification
      this.$notify({
        title: 'Logo Uploaded',
        message: 'Your header logo has been uploaded successfully',
        type: 'success',
        duration: 3000
      })
    },

    handleImageUpdate(imageData) {
      // Update the uploaded URL if changed
      if (imageData.url) {
        this.$set(this.componentData, 'uploadedUrl', imageData.url)
      }

      // Update ImageEditor data
      this.$set(this.componentData, 'imageEditorData', {
        url: imageData.url,
        description: imageData.description || '',
        scale: imageData.scale || 100,
        background_color: imageData.background_color || '#e8e7ec',
        object_fit: imageData.object_fit || 'cover',
        x: imageData.x || 0,
        y: imageData.y || 0,
        image_id: imageData.image_id || this.componentData.imageEditorData?.image_id || ''
      })

      // Update alt text if description changed
      if (imageData.description) {
        this.$set(this.componentData, 'imageAlt', imageData.description)
      }

      // Update metadata
      if (!this.componentData.imageMetadata) {
        this.$set(this.componentData, 'imageMetadata', {})
      }
      this.componentData.imageMetadata.lastUpdated = new Date().toISOString()
      if (imageData.image_id) {
        this.componentData.imageMetadata.imageId = imageData.image_id
      }

      this.$emit('update')

      // Show success notification
      this.$notify({
        title: 'Logo Updated',
        message: 'Your header logo has been updated successfully',
        type: 'success',
        duration: 3000
      })
    },

    clearImage() {
      this.$set(this.componentData, 'uploadedUrl', null)
      this.$set(this.componentData, 'imageSrc', '')
      this.$set(this.componentData, 'imageMetadata', null)
      this.$set(this.componentData, 'imageEditorData', null)
      this.$set(this.componentData, 'sourceType', 'url')

      this.$emit('update')

      this.$notify({
        title: 'Logo Removed',
        message: 'The header logo has been removed',
        type: 'info',
        duration: 2000
      })
    },

    openImageUpload() {
      // This will be triggered when clicking the placeholder
      console.log('Opening image upload for header')
    },

    onImageUploadClick() {
      console.log('Header image upload initiated')
    }
  }
}
</script>

<style scoped>
.header-component {
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  border-radius: 0;
  margin: 0 !important;
}

.header-component:hover,
.header-component.selected {
  border-color: #e14eca;
  box-shadow: 0 0 0 4px rgba(225, 78, 202, 0.1);
}

.header-content {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: inherit;
  position: relative;
}

.header-image-wrapper {
  position: relative;
  display: inline-block;
}

.header-image {
  display: block;
  max-width: 100%;
  height: auto;
}

.header-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 0.375rem;
  transition: all 0.2s;
}

.header-placeholder:hover {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.05);
}
</style>
